import { computarSaldoCapex } from "./capex/getSaldoCapexTarget";
import { claveTargetCapex, getSaldosCapexPorTarget } from "./capex/getSaldosCapexPorTarget";
import { client } from "./supabase";
import { exigirFechaValida, exigirFechaValidaEnLista } from "./validarFechaDocumento";

// ─── COSTOS DE LOTE (componentes de costo / landed cost de una compra) ────────
//
// Espejo estructural de planPagosActivos.js, pero colgado de lotes_compra(id) vía
// lote_costos.lote_id. Diferencia conceptual clave con las cuotas: las cuotas
// PARTICIONAN un mismo monto (Σ cuotas == costo_unitario, es financiación); los
// componentes de landed cost SUMAN al valor (FOB + transporte + aduana capitalizan
// a la base depreciable del activo). Cada componente lleva su propia moneda y su
// propia fecha_pago. Doctrina (ver ARQUITECTURA 9.36 / 4.13 / 10.100): pagar un
// componente es CAJA PURA (movimiento_bancario_id + fecha_pago_real + estado='pagado');
// la capitalización al activo va por la ejecución del lote, aparte.
//
// Fase A commit 1: solo datos + utils. Sin UI, sin proyección, sin conciliación.
//
// Fase 7a/7b (causa raíz, 10.1): un componente puede tener su PROPIO beneficiario, distinto del
// proveedor de la mercadería del lote (ej. la aduana la cobra el agente, no el proveedor
// extranjero). `lote_costos.proveedor_id` es NULLABLE, sin default: NULL = hereda el proveedor
// del lote (comportamiento histórico, 10.215), sin excepción.

// Proveedor EFECTIVO de un componente de lote: su propio `proveedor_id` (override) si lo tiene,
// si no el del lote (`lote.proveedor` embebido, o `lote.proveedor_id` crudo como fallback
// defensivo). ÚNICA fuente (10.1) — todo consumidor que atribuya un componente a un proveedor
// (modal de vinculación, Futuros, el tooltip de Históricos) pasa por acá. Acepta el componente
// con o sin el embed `proveedor`/`lote.proveedor` — devuelve `{ id, nombre }`, ambos `null` si no
// hay ningún proveedor resoluble (lote importado sin proveedor, caso legacy).
export const resolverProveedorComponente = (componente) => {
  if (componente?.proveedor_id != null) {
    return { id: componente.proveedor_id, nombre: componente.proveedor?.nombre ?? null };
  }
  return {
    id: componente?.lote?.proveedor?.id ?? componente?.lote?.proveedor_id ?? null,
    nombre: componente?.lote?.proveedor?.nombre ?? null,
  };
};

export const getCostosDeLote = async (loteId) => {
  const { data, error } = await client
    .from("lote_costos")
    .select("*")
    .eq("lote_id", loteId)
    // La fila sintética tipo='fob' (saldado directo del FOB, CapEx-IVA Fase 5) NO es un componente
    // de landed cost: es el FOB base (vive en lotes_compra) marcado saldado. Se excluye de este
    // editor de componentes para no duplicar el FOB ni dejar que el usuario la borre a mano.
    .neq("tipo", "fob")
    .order("fecha_pago")
    .order("id");
  if (error) throw error;
  return data || [];
};

// Set de lote_id que tienen AL MENOS un componente (cualquier estado). Necesario aparte de "tiene
// componentes pendientes": si el landed cost está completo (todos pagados), el lote sigue teniendo
// componentes y NO debe volver a proyectar la línea única del lote. Lo usará la bifurcación de CapEx.
export const getLotesConCostos = async (pais) => {
  const { data, error } = await client.from("lote_costos").select("lote_id").eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.lote_id));
};

// Set de lote_id cuyo FOB ya está SALDADO. CUTOVER Fase 2b: en vez de "existe una fila lote_costos
// tipo='fob'", el criterio pasa a "el target de esa fila FOB tiene saldo del ledger 0" (computarSaldoCapex
// sobre pagos_capex). Fase 3b-ii: devuelve un Map<lote_id, { saldada, saldoRestante, targetId, moneda }>
// (antes un Set de existencia) → expone el SALDO del target FOB, no solo saldado sí/no. Así Futuros
// muestra el FOB parcial con su saldo restante (no lo oculta ni muestra el total) y el picker lo ofrece
// para top-up. saldoRestante = objetivo − Σ monto_target vigente, en la moneda del target. Un lote SIN
// fila fob no está en el Map (proyecta el FOB completo desde lotes_compra). `saldosCapex` opcional
// (self-fetch batch si falta, sin N+1, 10.234). Regla 10.1 (una sola fuente).
export const getLotesFobSaldado = async (pais, { saldosCapex } = {}) => {
  const map = new Map();
  const { data, error } = await client
    .from("lote_costos")
    .select("lote_id, id, monto, moneda")
    .eq("pais", pais)
    .eq("tipo", "fob");
  if (error) throw error;
  if (!data || data.length === 0) return map;
  const saldos = saldosCapex || (await getSaldosCapexPorTarget(pais));
  for (const r of data) {
    const aplicado = saldos.get(claveTargetCapex("lote_costo", r.id))?.aplicadoTarget || 0;
    const { saldo, saldada } = computarSaldoCapex(Number(r.monto || 0), aplicado);
    map.set(r.lote_id, { saldada, saldoRestante: saldo, targetId: r.id, moneda: r.moneda });
  }
  return map;
};

// ─── Proyección "Futuros" — filtros puros (testeables sin mockear Supabase) ────
//
// Auditoría 3b/3c/4, Commit 1 (2026-09-25, 10.273): `lotesFobSaldado`/`componentesLotes` pueden ser
// `null` cuando su carga falló (ver catch de `getLotesFobSaldado`/`getCostosLotesFuturos` en
// FlujoCajaV2/index.js — nunca cae a `new Map()`/`[]`, que sería indistinguible de "cargó bien, nada
// pendiente"). Sin el libro no se puede saber qué FOB está saldado ni qué componente sigue pendiente,
// así que estos filtros devuelven `[]` explícito (no proyectan) en vez de reventar `.get`/`.filter`
// sobre `null` o mostrar un monto fantasma.

// FOB TOTALMENTE saldado → no proyectar la línea FOB. Un FOB PARCIAL (fila fob pendiente, saldo>0) NO
// se oculta: se muestra con su saldo restante (fuente única con el modal, Fase 3b-ii).
export const filtrarLotesCapexProyectables = (capexLotes, lotesFobSaldado, { incluirUSD, incluirLocal }) => {
  if (lotesFobSaldado === null) return [];
  return (capexLotes || [])
    .filter((l) => l.fecha_planificada)
    .filter((l) => (l.moneda === "USD" ? incluirUSD : incluirLocal))
    .filter((l) => !lotesFobSaldado.get(l.id)?.saldada);
};

// Componentes de costo: exigen fecha_pago (NOT NULL en DB, siempre presente) y respetan el filtro de
// moneda de la vista según la moneda propia del componente (que puede diferir de la del lote).
export const filtrarComponentesCapexProyectables = (componentesLotes, { incluirUSD, incluirLocal }) => {
  if (componentesLotes === null) return [];
  return (componentesLotes || [])
    .filter((c) => c.fecha_pago)
    .filter((c) => (c.moneda === "USD" ? incluirUSD : incluirLocal));
};

// F1 (B.2b, 2026-09-26): `fecha_pago` exige `modo` explícito en `opciones` — 10.159, un componente
// de lote SIEMPRE admite fecha pasada, así que el caller real (LoteCostosModal, guardarFechaLoteCapex)
// siempre pasa MODOS_VALIDACION.DATO_DOCUMENTO.
export const crearCostosLote = async (costos, opciones = {}) => {
  exigirFechaValidaEnLista(costos, "fecha_pago", opciones);
  const { data, error } = await client.from("lote_costos").insert(costos).select();
  if (error) throw error;
  return data || [];
};

// Edita UN componente por id. El objetivo (`monto`) está candado en la BASE (10.247,
// lote_costos_bloquea_objetivo_trg): la DB rechaza cambiarlo si el componente tiene pagos vigentes en
// `pagos_capex`. Los demás campos (tipo/moneda/fecha_pago) no afectan el saldo → no llevan candado; el
// viejo `.eq("estado","pendiente")` SOBRABA (el único campo sensible ya lo cubre 10.247, cutover 2a) y
// además habría bloqueado el flip pendiente→pagado del settlement. El frontend (LoteCostosModal, esPagado
// base-saldo) ya evita editar el objetivo de un target con pagos. El error del trigger propaga visible
// (10.177). Si el componente no existe, data vacío → error propio.
export const actualizarCostoLote = async (id, cambios, opciones = {}) => {
  exigirFechaValida(cambios, "fecha_pago", opciones);
  const { data, error } = await client.from("lote_costos").update(cambios).eq("id", id).select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo editar el costo ${id} (no existe).`);
  return data[0];
};

// Borra UN componente por id. El candado es la BASE (trigger BEFORE DELETE, migración
// 20260912_capex_candados_crud_delete_moneda_lote_activo.sql — 10.190/cutover 2a): la DB RECHAZA el
// borrado si el componente tiene un evento VIGENTE en `pagos_capex`. Ya NO se lee el flag `estado`; el
// error del trigger propaga visible (10.177/10.170). Si el componente no existe, data vacío → error propio.
export const eliminarCostoLote = async (id) => {
  const { data, error } = await client.from("lote_costos").delete().eq("id", id).select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar el costo ${id} (no existe).`);
  return data[0];
};

// ─── Componentes futuros pendientes (proyección CapEx) ────────────────────────
//
// Filtra estado='pendiente' + país; SIN filtro de fecha → incluye vencidos (patrón 10.155). Enriquece
// con el contexto del lote (descripción, cantidad, tipo, proveedor) y descarta componentes de lotes
// borrados (soft-delete no dispara el CASCADE). El consumidor (proyección CapEx) arma la línea por
// componente leyendo `c.lote`.
//
// FETCH EN DOS PASOS (deliberado, NO un embed `!inner`): `lote_costos` tiene RLS off, pero `lotes_compra`
// tiene RLS on. Un embed `lotes_compra!inner` acopla esta query al cache de relaciones FK de PostgREST
// y a la RLS del padre; con el FK recién creado, durante la ventana de refresco del cache el embed
// devolvía 400 y el componente desaparecía silenciosamente de CapEx. El patrón `.in(ids)` que ya usa
// getLotes es inmune a eso: dos queries planas sobre tablas que la app lee sin problema.
// CUTOVER Fase 2b: "pendiente" ya NO es el flag `estado`, es saldoRestante > tolerancia del subledger
// `pagos_capex` (computarSaldoCapex, en la moneda del componente). En todo-o-nada da IDÉNTICO al flag.
// El flag NO se apaga (Fase 4). Cada fila lleva `_saldoRestante` para el monto proyectado. `saldosCapex`
// opcional (self-fetch batch si falta, sin N+1 — el modal lo llama sin pasarlo).
export const getCostosLotesFuturos = async (pais, { saldosCapex } = {}) => {
  const { data: costos, error } = await client
    .from("lote_costos")
    // `proveedor` embebido = override PROPIO del componente (Fase 7a/10.1 — distinto proveedor
    // que el de la mercadería, ej. aduana cobrada por el agente). `select("*")` ya trae la
    // columna cruda `proveedor_id`; el embed resuelve su nombre sin round-trip aparte.
    .select("*, proveedor:proveedores(id, nombre)")
    .eq("pais", pais)
    // El FOB sintético (tipo='fob') NO es un componente de landed cost — el FOB base lo proyecta
    // mapCapexLote desde lotes_compra. Excluirlo evita doble conteo (CapEx-IVA Fase 5).
    .neq("tipo", "fob")
    .order("fecha_pago");
  if (error) throw error;
  if (!costos || costos.length === 0) return [];

  const loteIds = [...new Set(costos.map((c) => c.lote_id).filter((id) => id != null))];
  const { data: lotes, error: errLotes } = await client
    .from("lotes_compra")
    .select("id, pais, tipo_activo, descripcion, cantidad_total, moneda, eliminado, proveedor:proveedores(id, nombre)")
    .in("id", loteIds);
  if (errLotes) throw errLotes;

  const loteById = new Map((lotes || []).map((l) => [l.id, l]));
  const saldos = saldosCapex || (await getSaldosCapexPorTarget(pais));
  // eliminado=false client-side (soft-delete no dispara el CASCADE); saldada=false = pendiente por ledger.
  return costos
    .map((c) => {
      const aplicado = saldos.get(claveTargetCapex("lote_costo", c.id))?.aplicadoTarget || 0;
      const { saldo, saldada } = computarSaldoCapex(c.monto, aplicado);
      return {
        ...c,
        lote: loteById.get(c.lote_id) || null,
        _aplicadoTarget: aplicado,
        _saldoRestante: saldo,
        _saldada: saldada,
      };
    })
    .filter((c) => c.lote && c.lote.eliminado === false && !c._saldada);
};

// ─── Validación de orden de fechas de pago dentro de un lote ──────────────────
//
// Regla de negocio: FOB ≤ Transporte < Aduana.
//   · Transporte: fecha_pago ≥ FOB (pueden ser iguales).
//   · Aduana:     fecha_pago > la Transporte más tardía; si no hay Transporte (con fecha), > FOB (estricto).
//   · Otro:       sin restricción de orden.
// Helper PURO (sin efectos, testeable) a propósito: la Fase 2 (kebab en Caja) lo reusa. Solo compara
// filas con fecha cargada — las vacías no se validan acá (su obligatoriedad la maneja el submit).

// Normaliza a 'YYYY-MM-DD'. Las fechas ISO se comparan lexicográficamente == cronológicamente.
const soloFecha = (f) => (f && String(f).includes("T") ? String(f).substring(0, 10) : f || "");
// 'YYYY-MM-DD' → 'DD-MM-YYYY' para los mensajes al usuario.
const fechaHumana = (f) => {
  const [y, m, d] = String(f || "").split("-");
  return d ? `${d}-${m}-${y}` : f || "";
};

/**
 * Valida el orden de fechas de pago de un lote (FOB ≤ Transporte < Aduana).
 * @param {string} fobFecha fecha de pago del FOB ('YYYY-MM-DD' o ISO); si viene vacía no compara contra FOB.
 * @param {Array<{tipo:string, fecha_pago:string}>} componentes filas del form (estado actual).
 * @returns {Array<{idx:number, tipo:string, mensaje:string}>} violaciones (vacío si todo OK).
 */
export const validarOrdenFechasLote = (fobFecha, componentes = []) => {
  const violaciones = [];
  const fob = soloFecha(fobFecha);

  // Cota inferior de Aduana = la Transporte más tardía CON fecha cargada (si hay).
  const transporteMax = (componentes || []).reduce((max, c) => {
    if (c.tipo !== "transporte") return max;
    const f = soloFecha(c.fecha_pago);
    return f && f > max ? f : max;
  }, "");

  (componentes || []).forEach((c, idx) => {
    const f = soloFecha(c.fecha_pago);
    if (!f) return; // fila sin fecha: no se valida orden
    if (c.tipo === "transporte") {
      if (fob && f < fob) {
        violaciones.push({
          idx,
          tipo: c.tipo,
          mensaje: `El Transporte no puede ser anterior al FOB (FOB: ${fechaHumana(fob)}).`,
        });
      }
    } else if (c.tipo === "aduana") {
      const cota = transporteMax || fob; // > Transporte más tardía; si no hay, > FOB
      if (cota && f <= cota) {
        const ref = transporteMax ? "Transporte" : "FOB";
        violaciones.push({
          idx,
          tipo: c.tipo,
          mensaje: `La Aduana debe ser posterior al ${ref} (${ref}: ${fechaHumana(cota)}).`,
        });
      }
    }
    // 'otro': sin restricción de orden
  });

  return violaciones;
};
